import moment from 'moment-timezone';
import { generateRandomColor, getDefaultFacilitiesSelected, getDrawData, getDrawName, getParentDrawName, outcomeStringMap, resetColorIndex, shouldDisplayMainDrawAsChildDraw } from './courtSchedular';
import { drawCategoryTypeIds } from './eventConstants';
import { comparator, ComparatorTypes } from './comparator';

export const CourtGridItemWidth = 300;
export const CourtGridItemHeight = 220;
export const dateFormatForDrawDisplay = 'MMM, DD,YYYY, hh:mm a';
export const DoNotShowMatchStarted = 'do-not-show-match-started-popup';

export const ConfirmPopupTypes = {
    REMOVE: 'REMOVE',
    RE_ASSIGN: 'RE_ASSIGN',
};

export const SideBarDisplayTypes = {
    DRAWS: 'DRAWS',
    MATCHES: 'MATCHES',
};

export const DrawStatusTypes = {
    TODO: 1,
    LIVE: 2,
    DONE: 3,
};

export const DrawStatusNotificationTypes = {
    READY_TO_START: 1,
    AUTOFILL_NEEDED: 2,
    WAITING_FOR_LIVE_MATCHES: 3,
    MISSING_PLAYERS_TEAMS: 4,
};

export const MatchStatusTypes = {
    NONE: 0,
    TODO: 1,
    IN_QUEUE: 2,
    ON_COURT: 3,
    DONE: 4,
};

export const MatchSchedularStatusTypes = {
    IN_PROGRESS: 1,
    PAUSED: 2,
    COMPLETED: 3,
};

export const DrawStatusOptions = [
    {
        id: DrawStatusTypes.TODO,
        label: 'To do',
    },
    {
        id: DrawStatusTypes.LIVE,
        label: 'Live',
    },
    {
        id: DrawStatusTypes.DONE,
        label: 'Done',
    },
];

export const MatchStatusOptions = [
    {
        id: MatchStatusTypes.TODO,
        label: 'To do',
    },
    {
        id: MatchStatusTypes.IN_QUEUE,
        label: 'In queue',
    },
    {
        id: MatchStatusTypes.DONE,
        label: 'Done',
    },
];

export const ScoreSheetPrintOptions = {
    ON_QUEUE: 1,
    PLACED_ON_COURT: 2,
    NEVER: 3,
};

export const MatchStartOptions = {
    MANUAL: 1,
    PLACED_ON_COURT: 2,
};

export const ScoreSheetPrintOptionsList = [
    {
        id: ScoreSheetPrintOptions.ON_QUEUE,
        label: 'Match enters the matches queue',
    },
    {
        id: ScoreSheetPrintOptions.PLACED_ON_COURT,
        label: 'Match is placed on court',
    },
    {
        id: ScoreSheetPrintOptions.NEVER,
        label: 'Never',
    },
];

export const MatchStartOptionsList = [
    {
        id: MatchStartOptions.PLACED_ON_COURT,
        label: 'As soon as match is placed on court',
    },
    {
        id: MatchStartOptions.MANUAL,
        label: 'Use manual ‘play’ button',
    },
];

export const getDefaultValuesFromConfigObject = (config, eventSchedule, activeFacilitiesAndCourts, selectedDate = null) => {
    const { timeZone } = eventSchedule;

    const dateToUse = selectedDate || config.date;

    return {
        date: moment.utc(dateToUse).tz(timeZone?.value).toISOString(),
        facilities: getDefaultFacilitiesSelected(activeFacilitiesAndCourts, config),
        printScoreSheetType: config.printScoreSheetType,
        matchStartType: config.matchStartType,
        printCompletedScoreSheet: config.printCompletedScoreSheet,
        printMedalReport: config.printMedalReport,
    };
};

export const shouldDisplayMatchStatus = (match) => {
    return match.drawMatchStatus === MatchStatusTypes.DONE || match.drawMatchStatus === MatchStatusTypes.ON_COURT;
};

export const getMatchStatus = (match) => {
    switch (match.drawMatchStatus) {
        case MatchStatusTypes.ON_COURT:
            if (match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS) {
                return 'In progress';
            }
            if (match.schedulerStatus === MatchSchedularStatusTypes.PAUSED) {
                return 'Paused';
            }
            if (match.schedulerStatus === MatchSchedularStatusTypes.COMPLETED) {
                return 'Completed';
            }

            return 'Not started';
        case MatchStatusTypes.DONE: {
            const outcomeString = outcomeStringMap[match.result?.outcome];
            return outcomeString || match.result?.outcome || 'Completed';
        }
        default:
            return null;
    }
};

export const getUpdatedChildDraws = (parentDraw, drawCategoryTypesDict, color) => {
    let childDraws = [];

    if (parentDraw.childDraws?.length > 0) {
        const shouldDisplayMainDrawAsChild = shouldDisplayMainDrawAsChildDraw(parentDraw);
        if (shouldDisplayMainDrawAsChild) {
            childDraws.push({
                ...parentDraw,
                drawColor: color,
                customDrawId: `child-${parentDraw.id}`,
                childDraws: [],
                name: getDrawName(parentDraw, parentDraw, drawCategoryTypesDict),
                canEditDraw: false,
            });
        }

        const updatedChildDraws = parentDraw.childDraws?.map((childDraw) => {
            return {
                ...childDraw,
                drawColor: color,
                customDrawId: `child-${childDraw.id}`,
                childDraws: [],
                name: getDrawName(childDraw, parentDraw, drawCategoryTypesDict),
            };
        });
        childDraws = childDraws.concat(updatedChildDraws);
    }

    return childDraws;
};

export const updateSelectableStatus = (draws = []) => {
    return draws.map((parentDraw) => {
        let parentDrawIsSelectable = parentDraw.isSelectable;
        let parentDrawIsSelected = parentDraw.isSelected;

        if (parentDraw?.childDraws.length > 0) {
            parentDrawIsSelectable = parentDraw?.childDraws.reduce((acc, childDraw) => {
                return acc || childDraw.isSelectable;
            }, false);
            parentDrawIsSelected = parentDraw?.childDraws.reduce((acc, childDraw) => {
                return acc || childDraw.isSelected;
            }, false);
        }

        return {
            ...parentDraw,
            isSelectable: parentDrawIsSelectable,
            isSelected: parentDrawIsSelected,
        };
    });
};

export const getModifiedDrawsListForAddDrawPanel = (draws = [], drawCategoryTypesDict = {}, nextAvailable = {}, eventSchedule = {}) => {
    let updatedDraws = [];
    draws.forEach((draw) => {
        const isProDraw = draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW;
        const childDraws = getUpdatedChildDraws(draw, drawCategoryTypesDict)
            .filter((childDraw) => childDraw.isSelected || childDraw.isSelectable);

        // if pro draw split into different groups if not on same date
        if (isProDraw) {
            if (childDraws?.length > 0) {
                const groupedChildDraws = {
                    noDate: [],
                    sameDate: [],
                    previousDate: [],
                };
                childDraws.forEach((childDraw) => {
                    if (!childDraw.startDateTimeUtc) {
                        groupedChildDraws.noDate.push(childDraw);
                    } else if (isDrawStartedOnPreviousDay(childDraw, nextAvailable, eventSchedule)) {
                        groupedChildDraws.previousDate.push(childDraw);
                    } else {
                        groupedChildDraws.sameDate.push(childDraw);
                    }
                });
                Object.entries(groupedChildDraws).forEach(([date, group]) => {
                    if (group.length > 0) {
                        updatedDraws.push({
                            ...draw,
                            childDraws: group,
                            customDrawId: `parent-${date}-${draw.id}`,
                        });
                    }
                });
            } else {
                updatedDraws.push({
                    ...draw,
                    childDraws: [],
                    customDrawId: `parent-${draw.id}`,
                });
            }
        } else {
            updatedDraws.push({
                ...draw,
                childDraws,
                customDrawId: `parent-${draw.id}`,
            });
        }
    });

    updatedDraws = updateSelectableStatus(updatedDraws);

    const previouslyStartedDraws = [];
    const otherDraws = [];
    const datedDraws = [];
    const unDatedDraws = [];

    updatedDraws.forEach((draw) => {
        if (draw?.childDraws?.length > 0) {
            const previouslyStartedChildDraws = draw?.childDraws?.filter((childDraw) => {
                return isDrawStartedOnPreviousDay(childDraw, nextAvailable, eventSchedule);
            });
            if (previouslyStartedChildDraws.length > 0) {
                previouslyStartedDraws.push({
                    ...draw,
                });
            } else {
                otherDraws.push({
                    ...draw,
                });
            }
        } else if (isDrawStartedOnPreviousDay(draw, nextAvailable, eventSchedule)) {
            previouslyStartedDraws.push(draw);
        } else {
            otherDraws.push(draw);
        }
    });

    otherDraws.forEach((draw) => {
        if (draw?.childDraws?.length > 0) {
            const datedChildDraws = draw?.childDraws?.filter((childDraw) => {
                return childDraw?.startDateTimeUtc;
            });
            const unDatedChildDraws = draw?.childDraws?.filter((childDraw) => {
                return !childDraw?.startDateTimeUtc;
            });

            if (datedChildDraws.length > 0) {
                datedDraws.push({
                    ...draw,
                    childDraws: datedChildDraws,
                });
            }
            if (unDatedChildDraws.length > 0) {
                unDatedDraws.push({
                    ...draw,
                    childDraws: unDatedChildDraws,
                });
            }
        } else if (draw?.startDateTimeUtc) {
            datedDraws.push(draw);
        } else {
            unDatedDraws.push(draw);
        }
    });

    return {
        datedDraws,
        unDatedDraws,
        previouslyStartedDraws,
    };
};

export const getModifiedDrawsListForAddedDraws = (draws = [], drawCategoryTypesDict = {}) => {
    resetColorIndex();
    return draws.map((parentDraw) => {
        const color = generateRandomColor();
        const childDraws = getUpdatedChildDraws(parentDraw, drawCategoryTypesDict, color)
            .filter((childDraw) => childDraw.isSelected)
            .map((childDraw) => {
                return {
                    ...childDraw,
                    drawStatusToCheck: [childDraw.drawStatus],
                };
            });

        let drawStatusToCheck = [parentDraw.drawStatus];
        let { participantCount, matchCount, canStartDraw } = parentDraw;

        if (childDraws.length > 0) {
            delete parentDraw.isSelectable;
            delete parentDraw.isSelected;

            drawStatusToCheck = childDraws
                .reduce((acc, childDraw) => {
                    return acc.concat(childDraw.drawStatusToCheck);
                }, []);
            drawStatusToCheck = [
                ...new Set(drawStatusToCheck),
            ];

            participantCount = childDraws.reduce((acc, childDraw) => {
                return acc + childDraw.participantCount;
            }, 0);
            matchCount = childDraws.reduce((acc, childDraw) => {
                return acc + childDraw.matchCount;
            }, 0);
            canStartDraw = childDraws.reduce((acc, childDraw) => {
                return acc || childDraw.canStartDraw;
            }, false);
        }

        return {
            ...parentDraw,
            drawColor: color,
            childDraws,
            hasChildDraws: childDraws?.length > 0,
            customDrawId: `parent-${parentDraw.id}`,
            drawStatusToCheck,
            participantCount,
            matchCount,
            canStartDraw,
        };
    });
};

export const getUpdatedMatchObject = (match, drawsList = [], drawCategoryTypesDict = {}) => {
    const {
        drawId,
        facilityCourtId,
        ...others
    } = match;

    const { draw, parentDraw, color } = getDrawData(drawsList, drawId);

    return {
        drawId,
        drawCategoryTypeId: draw?.categoryTypeId,
        drawEliminationTypeId: draw?.eliminationTypeId,
        drawFormatTypeId: draw?.formatTypeId,
        drawBallTypeId: draw?.ballTypeId,
        drawIsTimeBased: draw?.isTimeBased,
        drawName: getDrawName(draw, parentDraw, drawCategoryTypesDict),
        parentDrawId: parentDraw?.id,
        parentDrawName: getParentDrawName(draw, parentDraw),
        drawColor: color,
        courtId: facilityCourtId,
        ...others,
    };
};

export const generateLocalStorageKey = (key, eventId, auth) => {
    return `${key}-${eventId}-${auth?.user?.id}`;
};

export const isMatchDraggable = (match) => {
    if (match.drawMatchStatus === MatchStatusTypes.ON_COURT) {
        return match.schedulerStatus !== MatchSchedularStatusTypes.IN_PROGRESS &&
            match.schedulerStatus !== MatchSchedularStatusTypes.COMPLETED;
    }

    return match.drawMatchStatus === MatchStatusTypes.IN_QUEUE;
};

export const canStartMatch = (match) => {
    if (match.drawMatchStatus === MatchStatusTypes.ON_COURT) {
        return match.schedulerStatus !== MatchSchedularStatusTypes.IN_PROGRESS &&
            match.schedulerStatus !== MatchSchedularStatusTypes.COMPLETED;
    }

    return false;
};

export const canPauseMatch = (match) => {
    if (match.drawMatchStatus === MatchStatusTypes.ON_COURT) {
        return match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS;
    }

    return false;
};

export const allowPostScore = (match) => {
    return match.drawMatchStatus === MatchStatusTypes.IN_QUEUE ||
        match.drawMatchStatus === MatchStatusTypes.ON_COURT ||
        match.drawMatchStatus === MatchStatusTypes.DONE;
};

export const shouldDisplayElapsedTimes = (match) => {
    if (match.drawMatchStatus === MatchStatusTypes.DONE) {
        return true;
    }

    return match.drawMatchStatus === MatchStatusTypes.ON_COURT &&
        (match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS ||
            match.schedulerStatus === MatchSchedularStatusTypes.PAUSED);
};

export const getElapsedTime = (match) => {
    if (match.drawMatchStatus === MatchStatusTypes.ON_COURT) {
        if (match.elaspsedTimeSeconds) {
            const elapsedTimeInMinutes = match.elaspsedTimeSeconds / 60;

            if (match.schedulerStatus === MatchSchedularStatusTypes.IN_PROGRESS && match.dateTimeReStarted) {
                const timeFromRestart = moment.utc().diff(moment.utc(match.dateTimeReStarted), 'minutes');
                return parseInt(elapsedTimeInMinutes, 10) + timeFromRestart;
            }

            return parseInt(elapsedTimeInMinutes, 10);
        }

        return `${moment.utc().diff(moment.utc(match.dateTimeStarted), 'minutes')}`;
    }

    return null;
};

export const formatElapsedTime = (elapsedTime) => {
    if (elapsedTime > 120) {
        const hours = Math.floor(elapsedTime / 60);
        const minutes = elapsedTime % 60;

        return `${hours}hrs ${minutes}min`;
    }

    return `${elapsedTime} min`;
};

export const isDrawStartedOnPreviousDay = (draw, nextAvailable, eventSchedule) => {
    const { selectedDateCourtConfig } = nextAvailable;

    if (draw.startDateTimeUtc) {
        const currentDate = moment(selectedDateCourtConfig.date).hours(0).minutes(0).seconds(0);
        const drawDate = moment.utc(draw.startDateTimeUtc).tz(eventSchedule.timeZone.value).hours(0).minutes(0)
            .seconds(0);

        return draw.isStartedOnPreviousDay || currentDate > drawDate;
    }

    return draw.isStartedOnPreviousDay;
};

export const isDrawPublished = (parentDraw) => {
    const shouldCheckPublished = parentDraw.categoryTypeId !== drawCategoryTypeIds.GROUPED;
    const parentDrawPublished = shouldCheckPublished
        ? parentDraw.isPublished && parentDraw.publishedStartDateTimeUtc === parentDraw.startDateTimeUtc
        : true;
    if (parentDraw.childDraws?.length > 0) {
        const areAllChildDrawsPublished = parentDraw.childDraws.reduce((acc, childDraw) => {
            return acc && childDraw.isPublished && childDraw.publishedStartDateTimeUtc === childDraw.startDateTimeUtc;
        }, true);

        return parentDrawPublished && areAllChildDrawsPublished;
    }

    return parentDrawPublished;
};

export const generatePublishPreviewDay = (nextAvailable) => {
    const {
        selectedFacilityId,
        selectedDateCourtConfig,
        facilitiesAvailable,
        draws,
    } = nextAvailable;

    const drawsToDisplayForDay = draws
        .reduce((acc, draw) => {
            let drawStartDate = draw.startDateTimeUtc;

            const isGroupedDraw = draw.categoryTypeId === drawCategoryTypeIds.GROUPED;
            const isProDraw = draw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW;

            if (isGroupedDraw || !drawStartDate) {
                drawStartDate = draw.childDraws.find((childDraw) => {
                    return childDraw.startDateTimeUtc;
                })?.startDateTimeUtc;
            }

            if (isProDraw) {
                const mainDraw = draw.childDraws.filter((childDraw) => childDraw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW);
                const qualifierDraws = draw.childDraws.filter((childDraw) => childDraw.categoryTypeId === drawCategoryTypeIds.PRO_DRAW_QUALIFIER);
                if (mainDraw.length > 0) {
                    acc.push({
                        ...draw,
                        startDateTimeUtc: drawStartDate,
                        inDraw: false,
                        clubLocationFacilityId: selectedFacilityId,
                        clubLocationFacilityName: facilitiesAvailable.find((facility) => facility.id === selectedFacilityId)?.name,
                    });
                }

                if (qualifierDraws.length > 0) {
                    qualifierDraws.forEach((qualifierDraw) => {
                        acc.push({
                            ...draw,
                            name: `${draw.name} - ${qualifierDraw.name}`,
                            startDateTimeUtc: qualifierDraw.startDateTimeUtc,
                            inDraw: true,
                            clubLocationFacilityId: selectedFacilityId,
                            clubLocationFacilityName: facilitiesAvailable.find((facility) => facility.id === selectedFacilityId)?.name,
                        });
                    });
                }
            } else {
                acc.push({
                    ...draw,
                    startDateTimeUtc: drawStartDate,
                    inDraw: false,
                    clubLocationFacilityId: selectedFacilityId,
                    clubLocationFacilityName: facilitiesAvailable.find((facility) => facility.id === selectedFacilityId)?.name,
                });
            }

            return acc;
        }, [])
        .sort(comparator([{ type: ComparatorTypes.FORMATTED_DATE, field: 'startDateTimeUtc', descend: false }]));

    return {
        date: selectedDateCourtConfig?.date,
        draws: drawsToDisplayForDay,
    };
};

export const compareDoneMatches = (match1, match2) => {
    const match1StartTime = moment.utc(match1.dateTimeStarted);
    const match2StartTime = moment.utc(match2.dateTimeStarted);
    const isBefore = match1StartTime.isBefore(match2StartTime, 'second', '()');
    const isSame = match1StartTime.isSame(match2StartTime, 'second', '()');

    if (isSame) {
        return 0;
    }

    return isBefore ? -1 : 1;
};

export const compareTodoMatches = (match1, match2) => {
    const match1TodoTime = moment.utc(match1.dateTimeTodo);
    const match2TodoTime = moment.utc(match2.dateTimeTodo);
    const isBefore = match1TodoTime.isBefore(match2TodoTime, 'second', '()');
    const isSame = match1TodoTime.isSame(match2TodoTime, 'second', '()');

    if (isSame) {
        const isSameDraw = match1.drawId === match2.drawId;
        if (isSameDraw) {
            const isSameRound = match1.roundNumber === match2.roundNumber;
            if (isSameRound) {
                return match1.matchPosition - match2.matchPosition;
            }

            return match1.roundNumber - match2.roundNumber;
        }

        return match1.drawCategoryTypeId - match2.drawCategoryTypeId;
    }

    return isBefore ? 1 : -1;
};

export const compareInQueueMatches = (match1, match2) => {
    const match1InQueueTime = moment.utc(match1.dateTimeInQueue);
    const match2InQueueTime = moment.utc(match2.dateTimeInQueue);
    const isBefore = match1InQueueTime.isBefore(match2InQueueTime, 'second', '()');
    const isSame = match1InQueueTime.isSame(match2InQueueTime, 'second', '()');

    if (isSame) {
        const isSameDraw = match1.drawId === match2.drawId;
        if (isSameDraw) {
            const isSameRound = match1.roundNumber === match2.roundNumber;
            if (isSameRound) {
                return match1.matchPosition - match2.matchPosition;
            }

            return match1.roundNumber - match2.roundNumber;
        }

        return match1.drawCategoryTypeId - match2.drawCategoryTypeId;
    }

    return isBefore ? -1 : 1;
};

export const compareMatches = (status) => (match1, match2) => {
    if (status.id === MatchStatusTypes.DONE) {
        return compareDoneMatches(match1, match2);
    }
    if (status.id === MatchStatusTypes.IN_QUEUE) {
        return compareInQueueMatches(match1, match2);
    }
    if (status.id === MatchStatusTypes.TODO) {
        return compareTodoMatches(match1, match2);
    }

    return 0;
};

export const getDrawStatusNotificationTextDetails = (notificationType = null, draw = null) => {
    const drawName = draw ? `${draw.name} ` : '';
    switch (notificationType) {
        case DrawStatusNotificationTypes.READY_TO_START:
            return {
                text: `${drawName}Ready to start`,
                color: '#007BCE',
            };
        case DrawStatusNotificationTypes.MISSING_PLAYERS_TEAMS:
            return {
                text: `${drawName}Missing players/teams`,
                color: '#D60C00',
            };
        case DrawStatusNotificationTypes.AUTOFILL_NEEDED:
            return {
                text: `${drawName}Autofill needed`,
                color: '#108B15',
            };
        case DrawStatusNotificationTypes.WAITING_FOR_LIVE_MATCHES:
            return {
                text: `${drawName}Waiting for live matches`,
                color: '#FB7800',
            };
        default:
            return {
                text: '',
                color: 'transparent',
            };
    }
};

const orderedNotificationStatuses = [
    DrawStatusNotificationTypes.READY_TO_START,
    DrawStatusNotificationTypes.AUTOFILL_NEEDED,
    DrawStatusNotificationTypes.WAITING_FOR_LIVE_MATCHES,
    DrawStatusNotificationTypes.MISSING_PLAYERS_TEAMS,
];

export const DefaultNotificationData = { shouldDisplay: false, text: '', color: 'transparent' };

export const getChildDrawNotificationData = (childDraws) => {
    const childDrawsWithNotification = childDraws
        .filter((childDraw) => childDraw.drawStatus === DrawStatusTypes.TODO && !!childDraw.drawStatusNotification);

    if (childDrawsWithNotification.length === 0) {
        return DefaultNotificationData;
    }

    const matchedStatus = orderedNotificationStatuses
        .find((type) => childDrawsWithNotification.some((childDraw) => childDraw.drawStatusNotification === type));

    if (matchedStatus) {
        const drawsWithMatchedStatus = childDrawsWithNotification
            .filter((childDraw) => childDraw.drawStatusNotification === matchedStatus);

        const drawToUse = drawsWithMatchedStatus.length === 1 ? drawsWithMatchedStatus[0] : null;

        const { text, color } = getDrawStatusNotificationTextDetails(
            matchedStatus,
            drawToUse,
        );

        return { shouldDisplay: true, text, color };
    }

    return DefaultNotificationData;
};

const getFlattenedDraws = (draws) => {
    return draws.reduce((acc, draw) => {
        if (draw.childDraws?.length > 0) {
            draw.childDraws.forEach((childDraw) => {
                acc[childDraw.id] = {
                    ...childDraw,
                    parentDraw: draw,
                };
            });
        } else {
            acc[draw.id] = draw;
        }
        return acc;
    }, {});
};

export const getDrawStatusChangesToNotify = (previousDraws = [], currentDraws = []) => {
    const previousDrawsMap = getFlattenedDraws(previousDraws);
    const currentDrawsMap = getFlattenedDraws(currentDraws);

    const drawsIdsToCheck = Object.keys(previousDrawsMap);
    const drawCompletedNotifications = [];
    const drawReadyToStartNotifications = [];
    const drawsToBeAutoFilledNotifications = [];

    drawsIdsToCheck.forEach((drawId) => {
        const previousDraw = previousDrawsMap[drawId];
        const currentDraw = currentDrawsMap[drawId];

        if (currentDraw.drawStatus === DrawStatusTypes.DONE && previousDraw.drawStatus !== DrawStatusTypes.DONE) {
            drawCompletedNotifications.push({
                drawId,
                drawCategoryTypeId: currentDraw.categoryTypeId,
                drawName: currentDraw.name,
                parentDrawId: currentDraw.parentDraw?.id,
                parentDrawName: currentDraw.parentDraw?.name,
                parentDrawCategoryTypeId: currentDraw.parentDraw?.categoryTypeId,
            });
        }

        if (currentDraw.drawStatus === DrawStatusTypes.TODO &&
            previousDraw.drawStatus === DrawStatusTypes.TODO &&
            currentDraw.drawStatusNotification === DrawStatusNotificationTypes.READY_TO_START &&
            previousDraw.drawStatusNotification !== DrawStatusNotificationTypes.READY_TO_START) {
            drawReadyToStartNotifications.push({
                drawId,
                drawCategoryTypeId: currentDraw.categoryTypeId,
                drawName: currentDraw.name,
                parentDrawId: currentDraw.parentDraw?.id,
                parentDrawName: currentDraw.parentDraw?.name,
                parentDrawCategoryTypeId: currentDraw.parentDraw?.categoryTypeId,
            });
        }

        if (currentDraw.drawStatus === DrawStatusTypes.TODO &&
            previousDraw.drawStatus === DrawStatusTypes.TODO &&
            currentDraw.drawStatusNotification === DrawStatusNotificationTypes.AUTOFILL_NEEDED &&
            previousDraw.drawStatusNotification !== DrawStatusNotificationTypes.AUTOFILL_NEEDED) {
            drawsToBeAutoFilledNotifications.push({
                drawId,
                drawCategoryTypeId: currentDraw.categoryTypeId,
                drawName: currentDraw.name,
                parentDrawId: currentDraw.parentDraw?.id,
                parentDrawName: currentDraw.parentDraw?.name,
                parentDrawCategoryTypeId: currentDraw.parentDraw?.categoryTypeId,
            });
        }
    });

    return {
        drawCompletedNotifications,
        drawReadyToStartNotifications,
        drawsToBeAutoFilledNotifications,
    };
};
